import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import SiteFormFields from "./SiteFormFields";
import RequiredFieldMarker from "./RequiredFieldMarker";
import studyService from "../../../services/studyService";

const SiteForm = ({
  onSubmit,
  isLoading,
  initialValues = {},
  title,
  mode = "add", // 'add' or 'edit'
}) => {
  const { studyID } = useParams();
  const [studyName, setStudyName] = useState("");
  const [values, setValues] = useState({
    site_code: initialValues.site_code || "",
    institution: initialValues.institution || "",
    country: initialValues.country || "United States",
    contact_person: initialValues.contact_person || "",
    email: initialValues.email || "",
    timezone: initialValues.timezone || "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!title && studyID) {
      studyService
        .getStudies()
        .then((studies) => {
          const study = studies.find((s) => s.id === studyID);
          if (study) setStudyName(study.title);
        })
        .catch(() => toast.error("Failed to load study info."));
    }
  }, [studyID, title]);

  const validate = () => {
    const newErrors = {};

    if (!/^\d{4}$/.test(values.site_code)) {
      newErrors.site_code = "Site code must be a 4-digit number";
    }

    if (!values.institution.trim()) {
      newErrors.institution = "Institution is required.";
    }

    if (!values.country.trim()) {
      newErrors.country = "Country is required.";
    }

    if (!values.contact_person.trim() || !values.contact_person.includes(" ")) {
      newErrors.contact_person = "Full name required.";
    }

    const strictEmailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!strictEmailRegex.test(values.email)) {
      newErrors.email = "Enter a valid email address.";
    }

    if (!values.timezone) {
      newErrors.timezone = "Timezone is required.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please correct all errors before submitting.");
      return;
    }
    onSubmit(values);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <Link
            to={`/studies/${studyID}#sites`}
            className="mb-4 flex items-center text-primary-600 hover:text-primary-700 transition-colors duration-200"
          >
            ← Back to Study
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl dark:shadow-gray-900/20 overflow-hidden"
        >
          {/* ✅ Green header */}
          <div className="px-6 py-4 bg-primary-600 dark:bg-primary-700">
            <h2 className="text-xl font-semibold text-white">
              {title || `Add New Site to Study: ${studyName}`}
            </h2>
          </div>

          {/* ✅ Form content */}
          <div className="px-6 py-8 sm:p-10">
            {/* ✅ Field instructions (outside header) */}
            <p className="text-sm text-gray-600 mb-4">
              Fields marked with <RequiredFieldMarker /> are required -
            </p>

            {/* ✅ Live site name preview */}
            {values.site_code && values.institution && (
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Site {values.site_code} - {values.institution}
                </h3>
                <div className="h-1 w-20 bg-primary-600"></div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <SiteFormFields
                values={values}
                errors={errors}
                onChange={handleChange}
                mode={mode}
              />

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => window.history.back()}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm dark:shadow-gray-900/20 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  {mode === "edit" ? "Save Changes" : "Add Site"}
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SiteForm;
